/**
 * Helpers for Files factory methods
 */

import type { IBaseNotary } from "../../BaseNotaryFactory";
import type { File } from "le-coffre-resources/dist/SuperAdmin";
import type { IRef } from "@Front/Api/BaseApiService";
import type { QueryPayload } from "../../../../types";
import type { IGetFilesparams, IGetFileByUidParams, IPutFilesParams } from "../Files";
import { downloadMultipleFiles, downloadAggregatedCertificate as downloadAggregatedCertificateHelper } from "../../../helpers/fileDownloadHelpers";
import { buildFilesUpdatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

/**
 * Create GET method for files
 */
export function createGetMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (q: IGetFilesparams): Promise<File[]> => {
		const url = new URL(baseUrl);
		const query = { q };
		if (q) baseNotary.appendJsonSearchParams(url, query);
		try {
			const files = await baseNotary.getRequest<File[]>(url);
			return files;
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for files
 */
export function createPostMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (body: FormData): Promise<File> => {
		const url = new URL(baseUrl);
		try {
			return await baseNotary.postRequestFormData<File>(url, body);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get upload link method
 */
export function createGetUploadLinkMethod(baseUrl: string) {
	return (uid: string): string => {
		return baseUrl.concat(`/download/${uid}`);
	};
}

/**
 * Create get download link method
 */
export function createGetDownloadLinkMethod(baseUrl: string) {
	return (uid: string, withCertificate = false): string => {
		const url = new URL(baseUrl.concat(`/download/${uid}`));
		if (withCertificate) {
			url.searchParams.set("with_certificate", "true");
		}
		return url.toString();
	};
}

/**
 * Create GET by UID method for files
 */
export function createGetByUidMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, q?: IGetFileByUidParams): Promise<File> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const query = { q: q as QueryPayload };
		if (q) baseNotary.appendJsonSearchParams(url, query);
		try {
			const file = await baseNotary.getRequest<File>(url);
			return file;
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method for files
 */
export function createPutMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, body: IPutFilesParams): Promise<File> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			const payload = buildFilesUpdatePayload(body);
			return await baseNotary.putRequest<File>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create DELETE method for files
 */
export function createDeleteMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<File> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			return await baseNotary.deleteRequest<File>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create download method for files
 */
export function createDownloadMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, withCertificate = false, ref?: IRef): Promise<Blob> => {
		const url = new URL(baseUrl.concat(`/download/${uid}`));
		if (withCertificate) {
			url.searchParams.set("with_certificate", "true");
		}
		try {
			return await baseNotary.getRequest<Blob>(url, { ref });
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create download multiple method for files
 */
export function createDownloadMultipleMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (fileUids: string[], includeAnchors = true, folderNumber?: string, folderName?: string): Promise<Blob> => {
		return downloadMultipleFiles(baseNotary, baseUrl, { fileUids, includeAnchors, folderNumber, folderName });
	};
}

/**
 * Create download aggregated certificate method for files
 */
export function createDownloadAggregatedCertificateMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (documentUids: string[], folderUid: string): Promise<Blob> => {
		return downloadAggregatedCertificateHelper(baseNotary, baseUrl, documentUids, folderUid);
	};
}
